import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { useConversations } from "@/hooks/useConversations";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) return;
    if (!context.auth.user) {
      throw redirect({ to: "/login" });
    }
  },
  component: AuthenticatedLayout,
});

const NAV_LINK_CLASS =
  "block w-full rounded px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-100";
const NAV_LINK_ACTIVE_CLASS =
  "block w-full rounded px-3 py-2 text-left text-sm bg-white font-medium text-gray-900";

function AuthenticatedLayout() {
  const { auth } = Route.useRouteContext();
  const {
    conversations,
    loading: sidebarLoading,
    refresh,
    create,
  } = useConversations();
  const navigate = Route.useNavigate();

  const handleNewChat = async () => {
    const id = await create();
    navigate({ to: "/c/$conversationId", params: { conversationId: id } });
    refresh();
  };

  return (
    <div className="flex h-screen bg-white">
      <aside className="flex w-64 flex-col border-r bg-gray-50">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-semibold text-gray-700">History</span>
          <button
            type="button"
            onClick={handleNewChat}
            className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-200"
          >
            + New
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto">
          {sidebarLoading ? (
            <div className="px-4 py-3 text-sm text-gray-400">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              No conversations yet
            </div>
          ) : (
            conversations.map((conv) => (
              <Link
                key={conv.id}
                to="/c/$conversationId"
                params={{ conversationId: conv.id }}
                className="block w-full truncate border-b border-gray-100 px-4 py-3 text-left text-sm text-gray-600 hover:bg-gray-100"
                activeProps={{
                  className:
                    "block w-full truncate border-b border-gray-100 px-4 py-3 text-left text-sm bg-white font-medium text-gray-900",
                }}
              >
                {conv.title || "Untitled"}
              </Link>
            ))
          )}
        </nav>
        <div className="space-y-1 border-t px-4 py-3">
          <Link
            to="/workflows"
            className={NAV_LINK_CLASS}
            activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
          >
            Workflows
          </Link>
          <Link
            to="/settings/integrations"
            className={NAV_LINK_CLASS}
            activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
          >
            Integrations
          </Link>
        </div>
      </aside>

      <main className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b px-6 py-3">
          <h1 className="text-lg font-semibold">Deft</h1>
          <button
            type="button"
            onClick={auth.logout}
            className="rounded px-3 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            Log out
          </button>
        </header>
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center text-gray-400">
              Loading...
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
